import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMap } from '../content/maps';
import type { MapId } from '../content/maps';
import { DEFAULT_THEME } from '../content/themes';
import { unitDef } from '../content/units';
import { drawMapBorder, drawTerrain } from './terrain';
import {
    drawBuildings,
    drawMoveTargets,
    drawPlacementGrid,
    drawProjectiles,
    drawSelectionBox,
    drawUnits,
    resetFacings,
} from './entities';
import { Effects } from './effects';
import { buildAtlas } from './atlas';
import { buildCameos } from './cameo';
import { buildTerrainAtlas } from './terrainAtlas';
import { cameraOnTile, clampCamera } from './camera';
import { useCameraControls } from './useCameraControls';
import type { BattlefieldInteraction, ScreenBox, WorldRect } from './useCameraControls';
import { worldToScreenX, worldToScreenY, worldToTile } from './projection';
import type { Camera, Viewport } from './projection';
import { useI18n } from '../i18n/context';
import { BuildPanel } from '../ui/BuildPanel';
import { buildQueueOf, canPlace, costOf, powerOf, prerequisitesMet } from '../sim/build';
import { UNIT_DEFS } from '../content/units';
import { buildingDef } from '../content/buildings';
import { PRODUCER_IDS, YARD_QUEUE } from '../content/production';
import { placementTiles } from './placement';
import { GameStore } from '../game/GameStore';
import { GameLoop } from '../game/GameLoop';
import { LocalCommandSource } from '../game/CommandSource';
import { AiCommandSource } from '../game/AiCommandSource';
import type { DifficultyId } from '../content/difficulties';
import { createGrid, createMatch } from '../sim/init';
import { deserialize, serialize } from '../sim/serialize';
import type { SavedGame } from '../sim/serialize';
import { formatHash, hashState } from '../sim/hash';
import { EventKind, Phase } from '../sim/types';
import { TILE } from '../sim/constants';
import { writeAutosave } from '../app/storage';
import { consumeEvents, disposeAudio, panFromScreenX, playSound, resumeAudio } from '../audio';
import { formatGameTime } from '../lib/gameTime';
import { createFogLayer, drawFog, updateFog } from './fog';
import { createBuildingMemory, createUnitSight, updateBuildingMemory, visibleEnemies } from './memory';
import { seesPoint, visibleMask } from '../sim/vision';
import { drawMinimap, minimapToWorld, resetMinimapCache } from './minimap';
import { ControlGroupBar } from '../ui/ControlGroupBar';
import { GROUP_COUNT, assignGroup, createGroups, groupSize, recallGroup } from './controlGroups';
import type { ControlGroups } from './controlGroups';
import type { MinimapLayout } from './minimap';
import { createAlerts, updateAlerts } from './alerts';
import type { ProductionItem, UnitId } from '../sim/types';

/*
 * מסך הקרב.
 *
 * שני דברים כאן אינם קוסמטיים:
 *   1. `dir="ltr"` על המכולה. בלעדיו מערכת הקואורדינטות מתהפכת יחד עם שפת הממשק,
 *      וּוקטורי גרירה ומיקומי יחידות מתהפכים איתה. זו הבאג ה-RTL הכי סביר בפרויקט.
 *   2. הציור והסימולציה חיים מחוץ ל-React לגמרי. React מרנדר את המכולה ואת ה-HUD;
 *      הקנבס מצויר מלולאת rAF שקוראת refs. בלי ההפרדה, כל טיק היה רינדור של React.
 */

const MAX_PIXEL_RATIO = 2;
const LOCAL_PLAYER = 0;

/** כל 400 טיקים = 20 שניות משחק. */
const AUTOSAVE_EVERY_TICKS = 400;

interface BattlefieldProps {
    readonly mapId: MapId;
    readonly difficulty: DifficultyId;
    readonly resumeFrom?: SavedGame | undefined;
    /** ה-seed של הקרב. מוצג ב-F3, ולכן דיווח באג ניתן לשחזור מדויק. */
    readonly seed: number;
    /** כמה שחקנים בקרב. שחקן 0 הוא האנושי; השאר מחשב. */
    readonly playerCount: number;
    readonly onExit: () => void;
    /** מתחיל קרב חדש על אותה מפה ובאותו קושי. אין = הכפתור אינו מוצג. */
    readonly onRestart?: (() => void) | undefined;
}

/** שחקן ריק לקריאות בטוחות לפני שהמצב נטען. */
const EMPTY_PLAYER = { queues: PRODUCER_IDS.map(() => []) as ProductionItem[][] };


/**
 * מיקום המפה המוקטנת: פינה שמאלית-תחתונה.
 *
 * **לא ימנית-עליונה** — שם כבר יושבים סרגל הבנייה, האשראי והחשמל. שתי הפינות
 * העליונות תפוסות, ולכן ההחלטה הזו נסגרת כאן ולא נגררת למלכודת הבאה.
 */
function minimapLayout(vp: Viewport): MinimapLayout {
    const size = Math.round(Math.min(120, Math.max(88, Math.min(vp.width, vp.height) * 0.26)));
    return { x: 8, y: vp.height - size - 8, size };
}

/**
 * ה-seed נגזר מהמפה ולא מהשעון: אותה מפה נותנת אותו משחק, וגם אחרי טעינה
 * מ-IndexedDB הוא זהה. כשיתווסף "משחק אקראי" הוא ייכנס דרך MatchConfig, לא כאן.
 */
/*
 * ה-seed מגיע **מבחוץ**, ולא נגזר מהמפה.
 *
 * קודם הוא היה `mapId.length * 7919 + 13`, ומזה נבעו שני דברים רעים: כל משחק
 * על אותה מפה היה זהה ביט-לביט — אותה הגרלה, אותו סדר, אותם גלים, ו"שחק שוב"
 * היה ממש אותו קרב — ושתי מפות באותו אורך שם חלקו seed.
 *
 * ההגרלה יושבת ב-`App.tsx` ולא כאן: הקוד הזה נטען גם בטסטים, ו-`Math.random`
 * בתוך רכיב הקרב היה הופך כל טסט שמרנדר אותו ללא-דטרמיניסטי.
 */

interface Hud {
    readonly fps: number;
    readonly tick: number;
    readonly hash: string;
    readonly zoom: number;
    readonly tileX: number;
    readonly tileY: number;
    readonly units: number;
    readonly selected: number;
    readonly paused: boolean;
    readonly ended: boolean;
    readonly winner: number;
    readonly speed: number;
    readonly attackMove: boolean;
    readonly placing: boolean;
    /** ההקשה האחרונה במצב הצבה נפלה על מקום פסול. מתאפס בהקשה מוצלחת. */
    readonly placeError: boolean;
    /** מצב קביעת נקודת התכנסות — חד-פעמי, כמו הצבה. */
    readonly rallying: boolean;
    /** כמה יחידות **חיות** בכל קבוצת שליטה. התווית על הכפתור. */
    readonly groups: readonly number[];
    /** התראת "הבסיס תחת התקפה" פעילה — באנר שהקשה עליו מקפיצה למקום. */
    readonly underAttack: boolean;
    /** הודעה חולפת שגוברת על טקסט הרמז הקבוע. ריק = אין. */
    readonly notice: string;
    /** שם המבנה שממתין להצבה, לתצוגה בשורת הסטטוס. */
    readonly placingName: string;
    /** סיכום הקרב, לתצוגה במסך הסיום. */
    readonly built: number;
    readonly lost: number;
    /** ההקשה הראשונה על "לתפריט" נלחצה — ההקשה הבאה באמת יוצאת. */
    readonly confirmExit: boolean;
    /** מזהה המבנה הנבחר, או ‎-1. */
    readonly selectedBuilding: number;
    /** מכירה נלחצה פעם אחת — ההקשה הבאה מוכרת באמת. */
    readonly confirmSell: boolean;
    /** שם המבנה הנבחר, לתצוגה. */
    readonly selectedBuildingName: string;
    /**
     * הרכב הבחירה — "3 גריזלי · 2 חי״ר" ולא רק מספר.
     *
     * בטלפון המסך מראה בערך 15×20 אריחים מתוך 96, ולכן **אתה לא רואה מה בחרת**.
     * "נבחרו 5 יחידות" אינו אומר אם יש לך חמישה חיילים או שני טנקים ושלושה
     * חיילים — וזו בדיוק ההחלטה אם לתקוף.
     */
    readonly composition: string;
    /**
     * אין מספיק אשראי לפריט שבראש תור כלשהו.
     *
     * נגזר בממשק ולא בסימולציה: `advanceQueue` מחזירה `spent: 0` והפס פשוט קופא,
     * ולכן "בונה לאט כי אין חשמל" ו"עצר לגמרי כי אין כסף" נראו זהים לחלוטין.
     */
    readonly starved: boolean;
    /** המבנה הנבחר פגוע — רק אז יש טעם בכפתור תיקון. */
    readonly selectedBuildingDamaged: boolean;
    readonly selectedBuildingRepairing: boolean;
    readonly credits: number;
    readonly power: { produced: number; consumed: number };
    /** תור לכל מבנה ייצור, לפי הסדר של `content/production.ts`. */
    readonly queues: readonly (readonly import('../sim/types').ProductionItem[])[];
}

export function Battlefield({
    mapId,
    difficulty,
    seed,
    playerCount,
    resumeFrom,
    onExit,
    onRestart,
}: BattlefieldProps) {
    const { t } = useI18n();
    const map = getMap(mapId);

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const viewportRef = useRef<Viewport>({ width: 0, height: 0 });
    const selectedRef = useRef<Set<UnitId>>(new Set());
    const dragBoxRef = useRef<ScreenBox | null>(null);
    /*
     * "תקוף-נוע" הוא מצב חד-פעמי של הפקודה הבאה, לא מתג קבוע על היחידה: לוחצים,
     * ואז מקישים על יעד. מתאושש מעצמו אחרי שימוש אחד, כדי שאי אפשר יהיה להישאר
     * בו בטעות.
     */
    const attackMoveRef = useRef(false);
    /*
     * מצב "הוסף לבחירה" — התחליף ל-Shift, שאינו קיים במגע. כל עוד הוא דלוק,
     * הקשה על יחידה מוסיפה או מסירה אותה מהבחירה במקום להחליף אותה.
     */
    /*
     * מצב הצבת מבנה: ההקשה הבאה על המפה מציבה את המבנה שהושלם, ולא שולחת פקודה.
     * מצב חד-פעמי, כמו תקוף-נוע.
     */
    const placingRef = useRef(false);
    /*
     * האריחים שבהם מותר להציב את המבנה שממתין. מחושבים מחדש רק כשמצב ההצבה נדלק
     * או כשמבנה נוסף — לא בכל פריים. `canPlace` סורק את כל המבנים לכל אריח, וזה
     * חישוב שאין שום סיבה לעשות 60 פעם בשנייה.
     */
    const placeTilesRef = useRef<number[]>([]);
    /*
     * מצב "נקודת התכנסות" — חד-פעמי, כמו תקוף-נוע והצבה. ההקשה הבאה על המפה
     * קובעת את הנקודה **לכל מבני הייצור של השחקן**. הסימולציה תומכת בנקודה לכל
     * מבנה בנפרד, אבל אין עדיין דרך לבחור מבנה בממשק — וקביעה אחת לכולם היא
     * ההתנהגות שמכסה את רוב המקרים בלי להמציא בורר.
     */
    const rallyingRef = useRef(false);
    /*
     * קבוצות שליטה — מצב ממשק, לא מצב משחק (ראו controlGroups.ts). ב-ref ולא
     * ב-state כי הן נקראות מתוך מטפלי אירועים ומהלולאה, ורק התוויות זקוקות
     * לרינדור מחדש.
     */
    const groupsRef = useRef<ControlGroups>(createGroups());
    /*
     * ערפל קרב. השכבה נבנית פעם אחת למשחק; המסכה של "נראה עכשיו" מחושבת פעם
     * **בטיק** ולא בפריים — היא תלויה רק במיקומים, וב-60fps מעל סימולציה של 20Hz
     * זה חוסך שני שלישים מהעבודה בלי שאף אחד יראה הבדל.
     */
    const fogRef = useRef<ReturnType<typeof createFogLayer> | null>(null);
    const visibleRef = useRef<Uint8Array>(new Uint8Array(0));
    const visibleTickRef = useRef(-1);
    const memoryTickRef = useRef(-1);
    /** המבנה הנבחר, או ‎-1. מצב ממשק — כמו הבחירה עצמה, אינו במצב המשחק. */
    const selectedBuildingRef = useRef<number>(-1);
    /** מה שהשחקן יודע על מבני האויב. ראו `render/memory.ts`. */
    const buildingMemoryRef = useRef(createBuildingMemory());
    /** מתי כל יחידת אויב נראתה לאחרונה — מונע ריצוד על שפת טווח הראייה. */
    const enemySightRef = useRef(createUnitSight());
    const minimapHitRef = useRef<HTMLDivElement | null>(null);
    const alertsRef = useRef(createAlerts());
    const alertRef = useRef<{ active: boolean; worldX: number; worldY: number } | null>(null);
    const fpsRef = useRef(0);
    const alphaRef = useRef(0);

    const [showDebug, setShowDebug] = useState(false);
    /*
     * התדריך מוצג רק בקרב חדש. מי שחזר דרך "המשך משחק" כבר יודע מה המטרה, ותדריך
     * שחוזר בכל טעינה הופך מהסבר להפרעה.
     */
    const [briefing, setBriefing] = useState(!resumeFrom);
    useEffect(() => {
        if (!briefing) return;
        const id = window.setTimeout(() => setBriefing(false), 6000);
        return () => window.clearTimeout(id);
    }, [briefing]);
    const [hud, setHud] = useState<Hud>({
        fps: 0,
        tick: 0,
        hash: '00000000',
        zoom: 0,
        tileX: 0,
        tileY: 0,
        units: 0,
        selected: 0,
        paused: false,
        ended: false,
        winner: -1,
        speed: 1,
        attackMove: false,
        placing: false,
        placeError: false,
        rallying: false,
        groups: [0, 0, 0],
        underAttack: false,
        notice: '',
        placingName: '',
        built: 0,
        lost: 0,
        confirmExit: false,
        selectedBuilding: -1,
        confirmSell: false,
        selectedBuildingName: '',
        composition: '',
        starved: false,
        selectedBuildingDamaged: false,
        selectedBuildingRepairing: false,
        credits: 0,
        power: { produced: 0, consumed: 0 },
        queues: [],
    });

    // הסימולציה נבנית פעם אחת לכל משחק. resumeFrom נקרא רק כאן — טעינה חוזרת
    // באמצע משחק הייתה דורסת את מה שהשחקן עשה מאז.
    const engine = useMemo(() => {
        const grid = createGrid(mapId);
        const initial = resumeFrom
            ? deserialize(resumeFrom)
            : createMatch({
                  seed,
                  mapId,
                  slots: [
                      { kind: 'human' as const },
                      ...Array.from({ length: Math.max(1, playerCount - 1) }, () => ({
                          kind: 'ai' as const,
                          difficulty,
                      })),
                  ],
              });
        const store = new GameStore(initial);
        const commands = new LocalCommandSource(LOCAL_PLAYER);
        /*
         * היריב הוא מקור פקודות שלישי לצד המקומי — לא מערכת מקבילה. זה מה שגורם
         * לו לעבור את אותה בדיקת חוקיות ואת אותו סדר פקודות כמו השחקן, ובדיוק
         * המקום שבו יתחבר יום אחד שחקן רשת.
         */
        /*
         * **מקור פקודות לכל יריב.** ‏`decide` מקבלת את מספר השחקן, ולכן שלושה
         * מחשבים הם שלושה מקורות ולא מערכת חדשה — וכל אחד מהם תוקף את השכן
         * הקרוב לו, כלומר גם זה את זה. זה כל ה-free-for-all.
         */
        const opponents = initial.players
            .filter((player) => player.kind === 'ai')
            .map((player) => new AiCommandSource(() => store.cur, grid, player.index));
        const loop = new GameLoop(store, grid, [commands, ...opponents]);
        return { grid, store, commands, loop };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapId, difficulty, seed, playerCount]);

    const mapSize = useMemo(() => ({ w: map.w, h: map.h }), [map.w, map.h]);

    const playerColors = useMemo(
        () => engine.store.cur.players.map((_p, i) => DEFAULT_THEME.playerColors[i] ?? '#ffffff'),
        [engine],
    );

    const atlasRef = useRef<ReturnType<typeof buildAtlas> | null>(null);
    if (!atlasRef.current) atlasRef.current = buildAtlas(playerColors);

    /*
     * אייקוני הקטלוג, בצבע השחקן המקומי. נבנים פעם אחת לכל משחק — הם `data:` URL,
     * ומכאן והלאה הדפדפן מטמן אותם כתמונות רגילות.
     */
    const cameos = useMemo(
        () => buildCameos(playerColors[LOCAL_PLAYER] ?? '#ffffff'),
        [playerColors],
    );

    /*
     * **המצלמה נפתחת על הבסיס שנבנה בפועל, ולא על מה שכתוב במפה.**
     *
     * מאז שנקודות הפתיחה מוגרלות (`sim/starts.ts`) אלה שני מקומות שונים, ומי
     * שקורא מהמפה פותח את המשחק על פינה ריקה. לכן ההגדרה יושבת **אחרי** בניית
     * המנוע — הוא מקור האמת, גם למשחק חדש וגם לטעינה משמירה.
     */
    const cameraRef = useRef<Camera>(
        (() => {
            const slot = engine.store.cur.startSlots[LOCAL_PLAYER];
            return cameraOnTile(slot?.x ?? map.w / 2, slot?.y ?? map.h / 2);
        })(),
    );

    const effectsRef = useRef<Effects | null>(null);
    if (!effectsRef.current) effectsRef.current = new Effects();

    // נבנה פעם אחת לכל המשחק — אינו תלוי במפה או בשחקנים.
    const terrainAtlasRef = useRef<ReturnType<typeof buildTerrainAtlas> | null>(null);
    if (!terrainAtlasRef.current) terrainAtlasRef.current = buildTerrainAtlas();

    /**
     * מבקש לצאת. ההקשה הראשונה משהה ומבקשת אישור, השנייה יוצאת.
     * גם הכפתור וגם Escape עוברים כאן — אחרת אחד מהם עוקף את השני.
     */
    const requestExit = useCallback(() => {
        setHud((value) => {
            if (value.confirmExit) return value;
            engine.loop.setPaused(true);
            return { ...value, confirmExit: true, notice: t('game_exit_confirm') };
        });
    }, [engine, t]);

    /**
     * האם נקודת עולם ידועה לשחקן המקומי.
     *
     * **אותו קריטריון בדיוק שלפיו היחידה מצוירת** — `explored`, לא `visible`.
     * שני מדדים שונים כאן פירושם יחידה שאתה רואה ואי אפשר להקיש עליה, או
     * להפך: הקשה על קרקע ריקה שהופכת לפקודת תקיפה על משהו שלא מצויר.
     */
    const seesUnit = useCallback(
        (worldX: number, worldY: number) => {
            const mask = engine.store.cur.players[LOCAL_PLAYER]?.explored;
            if (!mask || mask.length === 0) return true;
            return seesPoint(mask, map.w, worldX, worldY);
        },
        [engine, map.w],
    );

    /**
     * מבנה **שלי** תחת נקודת עולם.
     *
     * רק שלי בכוונה: בחירת מבנה קיימת כדי לפעול עליו (מכירה, נקודת התכנסות),
     * ובחירת מבנה של האויב לא הייתה עושה כלום — כלומר הקשה שנראית כמו באג.
     */
    const buildingAt = useCallback(
        (worldX: number, worldY: number): number => {
            const tileX = Math.floor(worldX / TILE);
            const tileY = Math.floor(worldY / TILE);
            for (const building of engine.store.cur.buildings) {
                if (building.owner !== LOCAL_PLAYER || building.hp <= 0) continue;
                const def = buildingDef(building.type);
                if (
                    tileX >= building.tileX &&
                    tileX < building.tileX + def.w &&
                    tileY >= building.tileY &&
                    tileY < building.tileY + def.h
                ) {
                    return building.id;
                }
            }
            return -1;
        },
        [engine],
    );

    /** יחידה תחת נקודת עולם, אם יש. */
    const unitAt = useCallback(
        (worldX: number, worldY: number): UnitId | null => {
            let best: UnitId | null = null;
            let bestDistance = Infinity;
            for (const unit of engine.store.cur.units) {
                /*
                 * **יחידת אויב שאינה נראית אינה קיימת מבחינת ההקשה.** בלי זה
                 * הקשה על קרקע שנראית ריקה, שבמקרה עומד בה קוצר אויב בצל, הפכה
                 * לפקודת תקיפה — הכוח דהר לתוך החושך, ואפילו הצליל (`attackOrder`
                 * במקום `moveOrder`) הסגיר שיש שם משהו.
                 */
                if (unit.owner !== LOCAL_PLAYER && !seesUnit(unit.x, unit.y)) continue;
                const def = unitDef(unit.type);
                const dx = unit.x - worldX;
                const dy = unit.y - worldY;
                const d2 = dx * dx + dy * dy;
                // מרווח נדיבות: באצבע קשה לפגוע ביחידה קטנה בדיוק.
                const reach = def.radius * 1.6;
                if (d2 <= reach * reach && d2 < bestDistance) {
                    bestDistance = d2;
                    best = unit.id;
                }
            }
            return best;
        },
        [engine],
    );

    /** כל היחידות שלי מסוג נתון שנמצאות בתוך המסך כרגע. */
    const visibleUnitsOfType = useCallback(
        (type: number) => {
            const camera = cameraRef.current;
            const viewport = viewportRef.current;
            return engine.store.cur.units.filter((unit) => {
                if (unit.owner !== LOCAL_PLAYER || unit.type !== type) return false;
                const x = worldToScreenX(unit.x, camera, viewport);
                const y = worldToScreenY(unit.y, camera, viewport);
                return x >= 0 && y >= 0 && x <= viewport.width && y <= viewport.height;
            });
        },
        [engine],
    );


    /**
     * הודעה חולפת בשורת הסטטוס.
     *
     * הטיימר יושב ב-ref ולא ב-state כדי שהודעה חדשה תאפס את הקודמת במקום לצבור
     * טיימרים — שתי הודעות ברצף היו גורמות לראשונה למחוק את השנייה.
     */
    const noticeTimer = useRef<number | null>(null);
    const setNotice = useCallback((text: string) => {
        setHud((value) => ({ ...value, notice: text }));
        if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
        noticeTimer.current = window.setTimeout(() => {
            noticeTimer.current = null;
            setHud((value) => ({ ...value, notice: '' }));
        }, 2600);
    }, []);

    useEffect(
        () => () => {
            if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
        },
        [],
    );

    /**
     * ממרכז את המצלמה על קבוצת יחידות.
     *
     * **זה לא נוחות — זה התסכול המרכזי במשחק טלפון.** המסך מראה בערך 15×20
     * אריחים מתוך 96, ולכן "בחרתי חמש יחידות" בלי לדעת איפה הן פירושו חמש
     * יחידות שאי אפשר לפקד עליהן. בחירה שאינה גוררת את המצלמה היא בחירה חלקית.
     */
    const centerOn = useCallback(
        (ids: readonly number[]) => {
            if (ids.length === 0) return;
            const wanted = new Set(ids);
            let sumX = 0;
            let sumY = 0;
            let count = 0;
            for (const unit of engine.store.cur.units) {
                if (!wanted.has(unit.id)) continue;
                sumX += unit.x;
                sumY += unit.y;
                count++;
            }
            if (count === 0) return;
            cameraRef.current = clampCamera(
                { ...cameraRef.current, x: Math.round(sumX / count), y: Math.round(sumY / count) },
                viewportRef.current,
                mapSize,
            );
        },
        [engine, mapSize],
    );

    /** האם היחידה נמצאת בתוך המסך כרגע. */
    const isOnScreen = useCallback((worldX: number, worldY: number) => {
        const camera = cameraRef.current;
        const viewport = viewportRef.current;
        const x = worldToScreenX(worldX, camera, viewport);
        const y = worldToScreenY(worldY, camera, viewport);
        return x >= 0 && y >= 0 && x <= viewport.width && y <= viewport.height;
    }, []);

    const selectAll = useCallback(() => {
        /*
         * **יחידות לחימה בלבד — לא קוצרים.**
         *
         * הרצף הטבעי בטלפון הוא "בחר הכול" ואז "תקוף-נוע", ועם הקוצרים בפנים הוא
         * שולח את כל הכלכלה למות בבסיס האויב בשתי הקשות. ההבחנה כבר קיימת בתוכן
         * (`weapon: null` ליחידה לא לוחמת) ואף אחד לא השתמש בה.
         */
        const chosen = engine.store.cur.units.filter(
            (unit) => unit.owner === LOCAL_PLAYER && unitDef(unit.type).weapon !== null,
        );
        const ids = chosen.map((unit) => unit.id);
        selectedRef.current = new Set(ids);
        setHud((value) => ({ ...value, selected: ids.length }));
        // ממרכז רק אם אף אחת מהן לא על המסך — אחרת קפיצה מיותרת בכל הקשה.
        if (!chosen.some((unit) => isOnScreen(unit.x, unit.y))) centerOn(ids);
    }, [engine, centerOn, isOnScreen]);

    /** מזהי היחידות החיות של השחקן — הבסיס לסינון קבוצות. */
    const livingIds = useCallback(
        () =>
            new Set(
                engine.store.cur.units
                    .filter((unit) => unit.owner === LOCAL_PLAYER)
                    .map((unit) => unit.id),
            ),
        [engine],
    );

    const refreshGroupCounts = useCallback(() => {
        const living = livingIds();
        setHud((value) => ({
            ...value,
            groups: Array.from({ length: GROUP_COUNT }, (_v, i) =>
                groupSize(groupsRef.current, i, living),
            ),
        }));
    }, [livingIds]);

    const assignToGroup = useCallback(
        (index: number) => {
            const size = selectedRef.current.size;
            groupsRef.current = assignGroup(groupsRef.current, index, selectedRef.current);
            refreshGroupCounts();
            playSound('select');
            setNotice(
                size === 0
                    ? `${t('game_group_cleared')} ${index + 1}`
                    : `${size} ${t('game_units')} ← ${t('game_groups')} ${index + 1}`,
            );
        },
        [refreshGroupCounts, setNotice, t],
    );

    const recallToSelection = useCallback(
        (index: number) => {
            const ids = recallGroup(groupsRef.current, index, livingIds());
            if (ids.length === 0) {
                /*
                 * קבוצה ריקה או מתה אינה מוחקת את מה שביד — ראו controlGroups.ts.
                 * במקום זה היא **מסבירה את עצמה**: בטלפון אין `title` ואין ריחוף,
                 * ולכן הקשה שלא עושה כלום היא הדרך היחידה שבה השחקן יגלה שהכפתור
                 * קיים — והיא בדיוק זו שגורמת לו לחשוב שהוא שבור.
                 */
                setNotice(`${t('game_group_empty')} ${index + 1}`);
                return;
            }
            selectedRef.current = new Set(ids);
            setHud((value) => ({ ...value, selected: ids.length }));
            playSound('select');
            centerOn(ids);
        },
        [livingIds, centerOn, setNotice, t],
    );

    /**
     * מחשב מחדש את האריחים החוקיים להצבה ומצייר אותם.
     * נסרק רק סביב המבנים של השחקן — טווח הבנייה ממילא מוגבל אליהם.
     */
    const refreshPlaceTiles = useCallback(() => {
        const head = buildQueueOf(engine.store.cur.players[LOCAL_PLAYER] ?? EMPTY_PLAYER)[0];
        placeTilesRef.current =
            placingRef.current && head?.ready
                ? placementTiles(
                      engine.store.cur,
                      engine.grid,
                      LOCAL_PLAYER,
                      head.type,
                      map.w,
                      map.h,
                  )
                : [];
    }, [engine, map.w, map.h]);

    /*
     * **מצב חד-פעמי אחד בכל רגע — ורק דרך הפונקציות האלה.**
     *
     * הצבה, נקודת התכנסות ותקוף-נוע כולם חוטפים את ההקשה הבאה על המפה,
     * ו-`onTap` בודקת אותם בסדר קבוע: `tryPlace` לפני `tryRally` לפני פקודת
     * קרקע. כלומר שני מצבים דלוקים יחד אינם "שני מתגים" אלא **באג**: ההקשה
     * הולכת לראשון בסדר, לא לזה שהשחקן הדליק אחרון.
     *
     * זה בדיוק מה שקרה: מתוך שלושה מקומות שהדליקו מצב, רק אחד כיבה את השני,
     * ולכן "הצב" ואז "נקודת התכנסות" מסרגל המבנה **הציב מבנה**. וגם הרשת
     * הירוקה נשארה מצוירת, כי `refreshPlaceTiles` — הניקוי היחיד — נקראה רק
     * מהמקום שכן טיפל בזה. שני הבאגים הם אותו באג: **מצב שאפשר לשנות בכמה
     * מקומות ישתנה לא נכון באחד מהם.**
     */
    const setPlacingMode = useCallback(
        (next: boolean) => {
            placingRef.current = next;
            if (next) rallyingRef.current = false;
            /*
             * הקריאה כאן מכסה את **שני** הכיוונים: כשהמצב נדלק היא מחשבת את
             * האריחים החוקיים, וכשהוא נכבה `placingRef` כבר false ולכן היא
             * מרוקנת אותם. הניקוי אינו שורה נפרדת שאפשר לשכוח.
             */
            refreshPlaceTiles();
            setHud((value) => ({
                ...value,
                placing: next,
                placeError: false,
                rallying: next ? false : value.rallying,
            }));
        },
        [refreshPlaceTiles],
    );

    const setRallyingMode = useCallback(
        (next: boolean) => {
            rallyingRef.current = next;
            if (next) placingRef.current = false;
            refreshPlaceTiles();
            setHud((value) => ({
                ...value,
                rallying: next,
                placing: next ? false : value.placing,
                placeError: false,
            }));
        },
        [refreshPlaceTiles],
    );

    /** פקודת קרקע: תנועה רגילה, או תקוף-נוע אם המצב החד-פעמי דלוק. */
    /** הצבת מבנה, אם אנחנו במצב הצבה. מחזירה true אם הפעולה נתפסה. */
    const tryPlace = useCallback(
        (worldX: number, worldY: number): boolean => {
            if (!placingRef.current) return false;
            const tileX = worldToTile(worldX);
            const tileY = worldToTile(worldY);
            const head = buildQueueOf(engine.store.cur.players[LOCAL_PLAYER] ?? EMPTY_PLAYER)[0];
            if (!head || !head.ready) return false;
            if (!canPlace(engine.store.cur, engine.grid, LOCAL_PLAYER, head.type, tileX, tileY)) {
                /*
                 * מיקום פסול — נשארים במצב הצבה במקום לזרוק את המבנה, **אבל
                 * אומרים למה.** קודם ההקשה פשוט לא עשתה כלום, וזה נראה כמו משחק
                 * תקוע: בר לא הצליח להוסיף מבנים ולא הייתה לו שום דרך להבין למה.
                 */
                setHud((value) => ({ ...value, placeError: true }));
                return true;
            }
            engine.commands.issue({ kind: 'place', unitIds: [], tileX, tileY }, engine.store.cur.tick);
            playSound('placed');
            setPlacingMode(false);
            return true;
        },
        [engine, setPlacingMode],
    );

    const issueGroundCommand = useCallback(
        (unitIds: UnitId[], worldX: number, worldY: number) => {
            const kind = attackMoveRef.current ? 'attackMove' : 'move';
            attackMoveRef.current = false;
            engine.commands.issue(
                { kind, unitIds, tileX: worldToTile(worldX), tileY: worldToTile(worldY) },
                engine.store.cur.tick,
            );
            /*
             * הצליל מנוגן **מיד**, לפני שהפקודה בכלל התבצעה. יש 100ms של השהיית
             * קלט ועוד המתנה ל-A*, ובחלון הזה שום דבר לא קורה על המסך — בטלפון
             * זה נקרא "לא נרשם" והשחקן מקיש שוב. משוב מיידי אינו עובר בסימולציה.
             */
            playSound(kind === 'attackMove' ? 'attackOrder' : 'moveOrder');
        },
        [engine],
    );

    /** קובע נקודת התכנסות לכל מבני הייצור. מחזירה true אם ההקשה נתפסה. */
    const tryRally = useCallback(
        (worldX: number, worldY: number): boolean => {
            if (!rallyingRef.current) return false;
            const tileX = worldToTile(worldX);
            const tileY = worldToTile(worldY);

            /*
             * **מבנה נבחר = הנקודה שלו בלבד.**
             *
             * הסימולציה תומכת בנקודה לכל מבנה מאז שהיא נכתבה, אבל הממשק קבע
             * אחת לכולם כי לא הייתה דרך לבחור מבנה. עכשיו יש: מפעל רק"ם ששולח
             * טנקים לחזית וקסרקטין ששולח חי"ר לשמור על הבסיס הם שתי כוונות
             * שונות. בלי מבנה נבחר — הכפתור בסרגל הבנייה — זו עדיין נקודה אחת
             * לכל מבני הייצור, וזה הדבר הנכון לרוב המקרים.
             */
            const selectedId = selectedBuildingRef.current;
            const producers = engine.store.cur.buildings.filter((building) => {
                if (building.owner !== LOCAL_PLAYER || building.hp <= 0) return false;
                if (!UNIT_DEFS.some((unit) => unit.producedBy === buildingDef(building.type).id)) {
                    return false;
                }
                return selectedId < 0 || building.id === selectedId;
            });
            for (const building of producers) {
                engine.commands.issue(
                    { kind: 'setRally', unitIds: [], buildingId: building.id, tileX, tileY },
                    engine.store.cur.tick,
                );
            }

            /*
             * **הבחירה נסגרת יחד עם המצב.** קודם `tryRally` חזרה מוקדם מ-`onTap`,
             * כלומר הניקוי של `selectedBuildingRef` שיושב אחריה לא רץ אף פעם —
             * ואחרי קביעת נקודה הפאנל נשאר פתוח בלי דרך ברורה לסגור אותו. זה
             * חלק ממה שבר דיווח עליו כ"ההודעה נשארת".
             */
            setRallyingMode(false);
            selectedBuildingRef.current = -1;
            setHud((value) => ({
                ...value,
                selectedBuilding: -1,
                confirmSell: false,
            }));
            return true;
        },
        [engine, setRallyingMode],
    );

    const interaction = useMemo<BattlefieldInteraction>(
        () => ({
            onBoxSelect: (rect: WorldRect, additive: boolean) => {
                const next = additive ? new Set(selectedRef.current) : new Set<UnitId>();
                for (const unit of engine.store.cur.units) {
                    if (unit.owner !== LOCAL_PLAYER) continue;
                    if (
                        unit.x >= rect.minX &&
                        unit.x <= rect.maxX &&
                        unit.y >= rect.minY &&
                        unit.y <= rect.maxY
                    ) {
                        next.add(unit.id);
                    }
                }
                selectedRef.current = next;
            },
            /*
             * הקשה אחת, ארבע משמעויות — לפי מה שמתחתיה ולפי סוג הקלט:
             *
             *   יחידה שלי      → בוחרים אותה, **תמיד**. גם כשיש כבר בחירה, אחרת אי
             *                    אפשר להחליף בחירה במגע (זה היה הבאג).
             *   יחידת אויב     → תקיפה, אם יש בחירה
             *   קרקע ריקה במגע → תנועה, אם יש בחירה. במגע אין כפתור ימני, ולכן
             *                    ההקשה חייבת לשמש כפקודה — ולריקון הבחירה יש כפתור.
             *   קרקע ריקה בעכבר → ריקון הבחירה, כמו בכל RTS
             */
            onTap: (worldX: number, worldY: number, isTouch: boolean, isDouble: boolean) => {
                if (tryPlace(worldX, worldY)) return;
                if (tryRally(worldX, worldY)) return;
                const id = unitAt(worldX, worldY);
                const unit = id === null ? null : engine.store.cur.units.find((u) => u.id === id);
                const unitIds = [...selectedRef.current].sort((a, b) => a - b);

                /*
                 * מבנה שלי נבחר רק כשאין יחידה מעליו וכשאין בחירה פעילה: אחרת
                 * הקשה על הבסיס בזמן שכוח נבחר הייתה מבטלת פקודת תנועה אליו —
                 * וזו פקודה לגיטימית לגמרי.
                 */
                if (!unit && unitIds.length === 0) {
                    const buildingId = buildingAt(worldX, worldY);
                    if (buildingId >= 0) {
                        selectedBuildingRef.current = buildingId;
                        /*
                         * **`confirmSell` מתאפס גם כאן.** בלעדיו האישור דלף בין
                         * מבנים: מאשרים מכירה על פילבוקס, מתחרטים, מקישים על
                         * המפעל — והכפתור כבר במצב "מכור באמת". הקשה בודדת הייתה
                         * מוכרת אותו בלי שום אישור, בדיוק מה שהמנגנון מונע.
                         */
                        setHud((value) => ({
                            ...value,
                            selectedBuilding: buildingId,
                            confirmSell: false,
                        }));
                        playSound('select');
                        return;
                    }
                }
                if (selectedBuildingRef.current >= 0) {
                    selectedBuildingRef.current = -1;
                    setHud((value) => ({ ...value, selectedBuilding: -1, confirmSell: false }));
                }

                if (unit && unit.owner === LOCAL_PLAYER) {
                    /*
                     * הקשה כפולה = כל היחידות מאותו סוג שנראות על המסך.
                     * במגע אין Shift, ולכן זו הדרך המהירה לבחור "כל הטנקים" בלי
                     * לגרור קופסה — והיא גם המוסכמה בכל RTS.
                     */
                    if (isDouble) {
                        selectedRef.current = new Set(
                            visibleUnitsOfType(unit.type).map((u) => u.id),
                        );
                        return;
                    }
                    selectedRef.current = new Set([unit.id]);
                    playSound('select');
                    return;
                }

                if (unit && unitIds.length > 0) {
                    engine.commands.issue(
                        { kind: 'attack', unitIds, targetId: unit.id },
                        engine.store.cur.tick,
                    );
                    playSound('attackOrder');
                    return;
                }

                if (isTouch && unitIds.length > 0) {
                    issueGroundCommand(unitIds, worldX, worldY);
                    return;
                }

                selectedRef.current = new Set();
            },
            onCommand: (worldX: number, worldY: number) => {
                if (tryPlace(worldX, worldY)) return;
                if (selectedRef.current.size === 0) return;
                const unitIds = [...selectedRef.current].sort((a, b) => a - b);

                // פקודה על אויב = תקיפה; על קרקע = תנועה. אותה לחיצה, שתי משמעויות,
                // בדיוק כמו בכל RTS.
                const hitId = unitAt(worldX, worldY);
                const hit = hitId === null ? null : engine.store.cur.units.find((u) => u.id === hitId);
                if (hit && hit.owner !== LOCAL_PLAYER) {
                    engine.commands.issue(
                        { kind: 'attack', unitIds, targetId: hit.id },
                        engine.store.cur.tick,
                    );
                    return;
                }

                issueGroundCommand(unitIds, worldX, worldY);
            },
            onDragBox: (box: ScreenBox | null) => {
                /*
                 * הרגע שבו הלחיצה הארוכה "נתפסה" הוא בדיוק הרגע שבו השחקן לא
                 * יודע אם קרה משהו — האצבע עדיין על המסך והקופסה עוד באפס שטח.
                 * הודעה קצרה כאן היא ההבדל בין פיצ'ר קיים לפיצ'ר שמתגלה.
                 */
                if (box !== null && dragBoxRef.current === null) {
                    setNotice(t('game_box_select'));
                    playSound('select');
                }
                dragBoxRef.current = box;
            },
        }),
        [
            engine,
            unitAt,
            buildingAt,
            visibleUnitsOfType,
            issueGroundCommand,
            tryPlace,
            setNotice,
            t,
        ],
    );

    const controls = useCameraControls(containerRef, cameraRef, viewportRef, mapSize, interaction);

    // מקשי מערכת: F3 דיבאג, רווח השהיה, Escape יציאה.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'F3') {
                event.preventDefault();
                setShowDebug((value) => !value);
            } else if (event.code === 'Space') {
                event.preventDefault();
                engine.loop.setPaused(!engine.loop.isPaused);
            } else if (event.key === 'Escape') {
                // Escape מרוקן בחירה קודם, ורק כשאין בחירה יוצא מהמשחק — ודרך
                // אותו אישור דו-שלבי של הכפתור, אחרת הוא עוקף בדיוק את מה שהוא
                // נבנה למנוע.
                if (selectedRef.current.size > 0) {
                    selectedRef.current = new Set();
                    setHud((value) => ({ ...value, selected: 0 }));
                } else {
                    requestExit();
                }
            } else if (event.key >= '1' && event.key <= String(GROUP_COUNT)) {
                /*
                 * מוסכמת ה-RTS: ספרה שולפת קבוצה, Ctrl+ספרה משייכת. במקלדת אין
                 * מה להמציא — מי שמשחק RTS ינסה בדיוק את זה.
                 */
                const index = Number(event.key) - 1;
                event.preventDefault();
                if (event.ctrlKey || event.metaKey) assignToGroup(index);
                else recallToSelection(index);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [engine, onExit, assignToGroup, recallToSelection]);

    // ציור + סימולציה
    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const atlas = atlasRef.current;
        const effects = effectsRef.current;
        const terrainAtlas = terrainAtlasRef.current;
        if (!container || !canvas || !atlas || !effects || !terrainAtlas) return;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;

        resetFacings();
        const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

        const resize = () => {
            const width = container.clientWidth;
            const height = container.clientHeight;
            if (width === 0 || height === 0) return;
            viewportRef.current = { width, height };
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(height * ratio);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            // חסימה כאן ולא רק בגרירה: המצלמה ההתחלתית נבנית לפני שידוע גודל
            // התצוגה, ובלי זה הפריים הראשון מראה שטח שחור מחוץ למפה.
            cameraRef.current = clampCamera(cameraRef.current, viewportRef.current, mapSize);
        };

        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(container);

        let frame = 0;
        let last = performance.now();
        let fpsAverage = 60;

        const loop = (now: number) => {
            const delta = Math.min(now - last, 250);
            last = now;
            if (delta > 0) fpsAverage += (1000 / delta - fpsAverage) * 0.1;
            fpsRef.current = fpsAverage;

            controls.applyKeyboardPan(delta);
            /*
             * **חריגה בתוך `advance` הייתה הורגת את הלולאה לצמיתות** — אין מי
             * שיקרא ל-`requestAnimationFrame` הבא, והמסך פשוט קופא. הסימולציה
             * דטרמיניסטית ולכן היא תיפול שוב באותו טיק, אבל לפחות הציור ממשיך
             * והשחקן רואה מצב במקום קנבס לבן.
             */
            try {
                engine.loop.advance(delta);
            } catch (error) {
                console.error('הסימולציה נפלה', error);
                engine.loop.setPaused(true);
            }
            alphaRef.current = engine.loop.alpha;

            const camera = cameraRef.current;
            const viewport = viewportRef.current;

            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            ctx.fillStyle = DEFAULT_THEME.colors.bg;
            ctx.fillRect(0, 0, viewport.width, viewport.height);

            // שכבת העפרות מגיעה ממצב המשחק ולא מהמפה — כשתתחיל כרייה, הציור יעקוב.
            drawTerrain(
                ctx,
                { w: map.w, h: map.h, terrain: map.terrain, ore: engine.store.cur.ore },
                camera,
                viewport,
                terrainAtlas,
            );
            drawMapBorder(ctx, map, camera, viewport, DEFAULT_THEME.colors.border);
            /*
             * האפקטים נבנים מאירועי הטיק — פלט חד-כיווני של הסימולציה.
             *
             * **מ-`drainEvents` ולא מ-`store.cur.events`.** ‏`cur` מחזיק רק את
             * אירועי הטיק האחרון, ופריים אינו טיק: ב-1x אותו אירוע נצרך שלוש
             * פעמים, וב-4x רוב האירועים נעלמו. ראו ההערה ב-GameLoop.
             */
            const events = engine.loop.drainEvents();
            effects.consume(events);
            /*
             * אותם אירועים בדיוק מזינים גם את הצליל. הפאן נגזר ממיקום האירוע על
             * המסך, ולכן פיצוץ בקצה השמאלי נשמע משמאל.
             */
            consumeEvents(
                events,
                (x) => panFromScreenX(worldToScreenX(x, camera, viewport), viewport.width),
                { isLocal: (event) => event.b === LOCAL_PLAYER },
            );
            // יחידה שיצאה — הודעה קצרה, כי הפאנל בדרך כלל כבר סגור.
            for (const event of events) {
                if (event.kind === EventKind.Spawned && event.b === LOCAL_PLAYER) {
                    setNotice(`${t(unitDef(event.a).nameKey)} ${t('game_unit_ready')}`);
                }
            }
            effects.update(delta);

            effects.drawGround(ctx, camera, viewport);
            // מתחת למבנים וליחידות: הרשת מראה איפה יש מקום, לא מסתירה מה כבר עומד.
            drawPlacementGrid(
                ctx,
                camera,
                viewport,
                placeTilesRef.current,
                map.w,
                DEFAULT_THEME.colors.success,
            );
            /*
             * מסכת הראייה מחושבת פעם בטיק. `store.cur` מתחלף רק כשטיק רץ, ולכן
             * ההשוואה ל-`tick` היא בדיוק "האם המצב זז מאז שחישבנו".
             */
            const state = engine.store.cur;
            if (visibleTickRef.current !== state.tick) {
                visibleTickRef.current = state.tick;
                visibleRef.current = visibleMask(
                    state,
                    LOCAL_PLAYER,
                    map.w,
                    map.h,
                    visibleRef.current,
                );
            }
            const explored =
                state.players[LOCAL_PLAYER]?.explored ?? new Uint8Array(map.w * map.h);
            /*
             * **מה שנחשף — נראה. ‏`explored` ולא `visible`.**
             *
             * בר: "הם מופיעים לי פתאום ואני לא יכול לראות את הדרך שהם עושים, גם
             * אם חשפתי את הערפל — פתאום פשוט מופיעה לי מתקפה." הקריטריון הקודם
             * היה מסכת "נראה **עכשיו**", כלומר 5-8 אריחים סביב הכוח: שטח שחשפת
             * לפני עשר דקות היה עדיין שטח עיוור, ומתקפה שחצתה אותו הגיעה בלי
             * שום אזהרה.
             *
             * זו ההשלמה של ההחלטה שערפל שהתפוגג אינו חוזר: אם החשיפה קבועה, גם
             * הראייה קבועה. **המחיר מוצהר** — אין עוד ערך מודיעיני בסיור חוזר,
             * ורואים כל התקפה מתגבשת. זו הייתה הבחירה של בר בשתי הפעמים.
             *
             * מנגנון הידיעה הישנה (`render/memory.ts`) נשאר ומטפל במה שנשאר:
             * יחידה שנכנסת לשטח שמעולם לא נחשף.
             */
            if (visibleTickRef.current === state.tick && memoryTickRef.current !== state.tick) {
                memoryTickRef.current = state.tick;
                updateBuildingMemory(
                    buildingMemoryRef.current,
                    state,
                    LOCAL_PLAYER,
                    explored,
                    map.w,
                );
            }

            const fogView = {
                localPlayer: LOCAL_PLAYER,
                mapWidth: map.w,
                visible: visibleRef.current,
                explored,
                remembered: buildingMemoryRef.current,
                enemyAlpha: visibleEnemies(
                    enemySightRef.current,
                    state.units,
                    LOCAL_PLAYER,
                    explored,
                    map.w,
                    TILE,
                    now,
                ),
            };

            drawBuildings(ctx, camera, viewport, state, playerColors, fogView);
            drawMoveTargets(ctx, camera, viewport, engine.store.cur, selectedRef.current, map.w);
            drawUnits(ctx, camera, viewport, {
                cur: engine.store.cur,
                prev: engine.store.prev,
                alpha: engine.loop.alpha,
                atlas,
                playerColors,
                selected: selectedRef.current,
                localPlayer: LOCAL_PLAYER,
                fog: fogView,
            });
            drawProjectiles(
                ctx,
                camera,
                viewport,
                engine.store.cur,
                engine.store.prev,
                engine.loop.alpha,
                playerColors,
            );
            effects.drawAir(ctx, camera, viewport);
            if (dragBoxRef.current) drawSelectionBox(ctx, dragBoxRef.current);

            /*
             * המפה המוקטנת אחרונה — היא יושבת מעל שדה הקרב. הפינה השמאלית-תחתונה
             * ולא הימנית-עליונה: שם כבר יושבים סרגל הבנייה, האשראי והחשמל.
             */
            /*
             * הערפל מצויר **אחרי** הישויות והאפקטים ולפני המפה המוקטנת: הוא
             * מכסה את שדה הקרב, לא את הממשק.
             */
            if (!fogRef.current) fogRef.current = createFogLayer(map.w, map.h);
            updateFog(fogRef.current, explored);
            drawFog(ctx, fogRef.current, camera, viewport, map);

            const layout = minimapLayout(viewport);
            /*
             * שכבת ההקשה נמתחת מהמידות **המחושבות** ולא ממשתנה CSS.
             * ה-CSS מדד `vmin` — כלומר את החלון — בזמן ש-`minimapLayout` מודדת את
             * המכולה, שנמוכה ממנו בגובה הסרגל. בלנדסקייפ ההפרש יצר רצועה של ~10px
             * שבלעה הקשות ולא עשתה כלום, בדיוק בשפה שאליה האצבע מכוונת.
             */
            const hit = minimapHitRef.current;
            if (hit) {
                hit.style.width = `${layout.size}px`;
                hit.style.height = `${layout.size}px`;
                hit.style.insetInlineStart = `${layout.x}px`;
                hit.style.insetBlockEnd = `${viewport.height - layout.y - layout.size}px`;
            }
            drawMinimap(ctx, layout, {
                map: { w: map.w, h: map.h, terrain: map.terrain },
                ore: engine.store.cur.ore,
                state: engine.store.cur,
                camera,
                viewport,
                playerColors,
                localPlayer: LOCAL_PLAYER,
                fog: fogView,
            });

            /*
             * התראת "הבסיס מותקף". היום מגלים שהותקפת רק כשמגיעים לשם — ועל מפה
             * של 96 אריחים זה בדרך כלל מאוחר מדי.
             */
            const wasActive = alertRef.current?.active ?? false;
            const alert = updateAlerts(alertsRef.current, engine.store.cur, LOCAL_PLAYER, delta);
            alertRef.current = alert;
            // הקצה העולה בלבד — הצינון של 8 שניות כבר בתוך updateAlerts.
            if (alert?.active && !wasActive) playSound('deathBuilding');

            frame = requestAnimationFrame(loop);
        };

        frame = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            resetMinimapCache();
        };
    }, [engine, map, mapSize, playerColors, controls, setNotice, t]);

    /*
     * שמירה אוטומטית.
     *
     * גם על פי טיקים וגם על יציאה מהמסך — iOS הורג טאבים ברקע בלי התראה, ובלי
     * ה-`pagehide` בדיקה בטלפון מאבדת משחק באמצע.
     */
    useEffect(() => {
        let lastSavedTick = -1;

        const save = () => {
            const state = engine.store.cur;
            /*
             * **משחק שנגמר אינו נשמר.** בלי זה היציאה לתפריט אחרי ניצחון שמרה את
             * המצב הגמור, ו"המשך משחק" טען ישר לתוך מסך "ניצחון" של קרב שאין מה
             * להמשיך בו — עם זמן משחק ליד הכפתור, כאילו יש שם משהו.
             */
            if (state.phase === Phase.Ended) return;
            if (state.tick === lastSavedTick) return;
            lastSavedTick = state.tick;
            void writeAutosave(serialize(state), Date.now());
        };

        const timer = setInterval(() => {
            const tick = engine.store.cur.tick;
            if (tick > 0 && tick - lastSavedTick >= AUTOSAVE_EVERY_TICKS) save();
        }, 1000);

        const onHide = () => {
            if (document.visibilityState === 'hidden') save();
        };
        window.addEventListener('pagehide', save);
        document.addEventListener('visibilitychange', onHide);

        return () => {
            clearInterval(timer);
            window.removeEventListener('pagehide', save);
            document.removeEventListener('visibilitychange', onHide);
            save();
            // סוגר את הקשר האודיו ומאפס מונים — אחרת מונה הקולות נגרר למשחק הבא.
            disposeAudio();
        };
    }, [engine]);

    /*
     * רענון ה-HUD ב-4Hz. הנתונים נקראים מה-refs ומהחנות ישירות ולא מתוך לולאת
     * הציור: כשהטאב ברקע rAF לא נקרא, ופאנל שמתעדכן מתוך הלולאה היה מציג מצב
     * קפוא בדיוק כשצריך אותו. פאנל דיבאג שמשקר גרוע מפאנל שאין.
     */
    useEffect(() => {
        const id = setInterval(() => {
            const camera = cameraRef.current;
            const state = engine.store.cur;
            /*
             * שטח ההצבה מתרענן כאן ולא בלולאת הציור: הוא משתנה רק כשמבנה נוסף או
             * נהרס, וסריקה של מאות אריחים ב-60fps היא בזבוז מוחלט.
             */
            if (placingRef.current) refreshPlaceTiles();
            /*
             * ספירת הקבוצות מתרעננת כאן ולא ברגע המוות: אין למי להאזין, והצטלבות
             * שלוש רשימות קצרות ארבע פעמים בשנייה זולה מכל מנגנון התראה.
             */
            const living = livingIds();
            setHud((value) => ({
                ...value,
                fps: fpsRef.current,
                tick: state.tick,
                // ‏`hashState` עובר על כל אריחי המפה לכל שחקן מאז שנוסף `explored`.
                // אין סיבה לשלם את זה ארבע פעמים בשנייה כשהפאנל סגור.
                hash: showDebug ? formatHash(hashState(state)) : value.hash,
                zoom: camera.zoom,
                tileX: worldToTile(camera.x),
                tileY: worldToTile(camera.y),
                units: state.units.length,
                selected: selectedRef.current.size,
                composition: (() => {
                    if (selectedRef.current.size === 0) return '';
                    const counts = new Map<number, number>();
                    for (const unit of state.units) {
                        if (!selectedRef.current.has(unit.id)) continue;
                        counts.set(unit.type, (counts.get(unit.type) ?? 0) + 1);
                    }
                    // סדר קבוע לפי סוג — אחרת השורה מתערבבת בכל רענון.
                    return [...counts.entries()]
                        .sort((a, b) => b[1] - a[1] || a[0] - b[0])
                        .map(([type, count]) => `${count} ${t(unitDef(type).nameKey)}`)
                        .join(' · ');
                })(),
                paused: engine.loop.isPaused,
                ended: state.phase === Phase.Ended,
                winner: state.winner,
                speed: engine.loop.speed,
                attackMove: attackMoveRef.current,
                placing: placingRef.current,
                selectedBuildingName: (() => {
                    const id = selectedBuildingRef.current;
                    if (id < 0) return '';
                    const found = state.buildings.find((b) => b.id === id);
                    // מבנה שנהרס בזמן שהיה נבחר — הבחירה מתנקה מעצמה.
                    if (!found) {
                        selectedBuildingRef.current = -1;
                        return '';
                    }
                    return t(buildingDef(found.type).nameKey);
                })(),
                starved: (() => {
                    const me = state.players[LOCAL_PLAYER];
                    if (!me) return false;
                    for (const queue of me.queues) {
                        const item = queue[0];
                        if (!item || item.ready) continue;
                        if (me.credits <= 0) return true;
                    }
                    return false;
                })(),
                selectedBuildingDamaged: (() => {
                    const found = state.buildings.find(
                        (b) => b.id === selectedBuildingRef.current,
                    );
                    return found !== undefined && found.hp < buildingDef(found.type).hp;
                })(),
                selectedBuildingRepairing:
                    state.buildings.find((b) => b.id === selectedBuildingRef.current)
                        ?.repairing === 1,
                selectedBuilding: state.buildings.some(
                    (b) => b.id === selectedBuildingRef.current,
                )
                    ? selectedBuildingRef.current
                    : -1,
                // מבנה שנעלם מנקה גם את האישור, ולא רק את הבחירה.
                confirmSell: state.buildings.some((b) => b.id === selectedBuildingRef.current)
                    ? value.confirmSell
                    : false,
                placingName: (() => {
                    const head = buildQueueOf(state.players[LOCAL_PLAYER] ?? EMPTY_PLAYER)[0];
                    return head ? t(buildingDef(head.type).nameKey) : '';
                })(),
                rallying: rallyingRef.current,
                groups: Array.from({ length: GROUP_COUNT }, (_v, i) =>
                    groupSize(groupsRef.current, i, living),
                ),
                underAttack: alertRef.current?.active ?? false,
                built: state.players[LOCAL_PLAYER]?.unitsBuilt ?? 0,
                lost: state.players[LOCAL_PLAYER]?.unitsLost ?? 0,
                credits: state.players[LOCAL_PLAYER]?.credits ?? 0,
                power: powerOf(state.buildings, LOCAL_PLAYER),
                queues: state.players[LOCAL_PLAYER]?.queues ?? [],
                
            }));
        }, 250);
        return () => clearInterval(id);
    }, [engine, refreshPlaceTiles, livingIds, showDebug, t]);


    return (
        <div className="game">
            {/* dir="ltr" מקובע — מערכת הקואורדינטות לא תלויה בשפת הממשק. */}
            <div
                className="game__canvasWrap"
                ref={containerRef}
                dir="ltr"
                /*
                 * דפדפנים חוסמים אודיו עד למגע אמיתי של המשתמש, ו-iOS מחמיר במיוחד.
                 * זו הנקודה הראשונה שבה בטוח יש מגע כזה. בטוח לקריאה חוזרת.
                 */
                onPointerDown={() => void resumeAudio()}
            >
                <canvas ref={canvasRef} className="game__canvas" />

                {/*
                    שכבת ההקשה של המפה המוקטנת.
                    ‏`stopPropagation` הוא מה שמונע מההקשה להיות גם פקודת תנועה,
                    ומלכודת 33 מכוסה: זה אינו קנבס, ולכן שכבת הקלט מתעלמת ממנו.
                */}
                <div
                    className="game__minimapHit"
                    ref={minimapHitRef}
                    onPointerDown={(event) => {
                        event.stopPropagation();
                        const container = containerRef.current;
                        if (!container) return;
                        const box = container.getBoundingClientRect();
                        const layout = minimapLayout(viewportRef.current);
                        const world = minimapToWorld(
                            layout,
                            event.clientX - box.left,
                            event.clientY - box.top,
                            mapSize,
                        );
                        if (!world) return;
                        cameraRef.current = clampCamera(
                            { ...cameraRef.current, x: world.x, y: world.y },
                            viewportRef.current,
                            mapSize,
                        );
                    }}
                />

                {/*
                    הסטטוס יושב מעל הקנבס ולא בסרגל הכפתורים. בטלפון צר הם נלחמו על
                    אותו רוחב: שם המפה נצמד לכפתור והסטטוס נחתך ל"נבחר...".
                    כאן יש לו שורה משלו, ו-pointer-events: none כדי שלא יחסום גרירה.
                */}
                {/*
                    תדריך פתיחה.

                    **אף אחד לא אמר לשחקן מה המטרה.** הוא נחת בקרב עם רמז אחד על
                    בחירה, בלי לדעת מה מנצח, מה זה 5,000, מה זה ⚡70, ושהמכונה
                    האפורה למטה היא כל הכלכלה שלו. ארבע שניות ואז נעלם מעצמו —
                    מסך שצריך לסגור הוא מכשול, לא הסבר.
                */}
                {briefing && (
                    <div className="game__briefing" role="status">
                        <span>{t('brief_goal')}</span>
                        <span>{t('brief_economy')}</span>
                        <span>{t('brief_power')}</span>
                        <span>{t('brief_select')}</span>
                    </div>
                )}

                {/*
                    התראת "הבסיס תחת התקפה". באנר ולא רק צליל: בטלפון המסך מראה
                    פינה אחת של מפת 96 אריחים, והקשה עליו מקפיצה את המצלמה לאירוע —
                    זה מה שהופך את ההתראה למשהו שאפשר להגיב לו ולא רק לשמוע.
                */}
                {hud.underAttack && (
                    <button
                        className="game__alert"
                        type="button"
                        onClick={() => {
                            const alert = alertRef.current;
                            if (!alert) return;
                            // דרך clampCamera ולא בהשמה ישירה — קפיצה לקצה המפה
                            // הייתה מוציאה את המצלמה מהתחום החוקי.
                            cameraRef.current = clampCamera(
                                { ...cameraRef.current, x: alert.worldX, y: alert.worldY },
                                viewportRef.current,
                                mapSize,
                            );
                        }}
                    >
                        {t('game_under_attack')}
                    </button>
                )}

                {/*
                    פעולות על מבנה נבחר.

                    **שורה נפרדת שמופיעה רק כשיש מבנה נבחר**, ולא כפתורים בסרגל
                    הקבוע: הסרגל כבר נשבר שלוש פעמים מלחץ של עוד כפתור (מלכודת 19),
                    וכפתור שמופיע ונעלם בתוכו מזיז את כל השאר מתחת לאצבע (27).
                    כאן זו שכבה מעל שדה הקרב, שלא נוגעת בסרגל בכלל.
                */}
                {hud.selectedBuilding >= 0 && (
                    <div className="game__buildingBar">
                        {/*
                            **כפתור סגירה מפורש.** הפאנל נסגר גם בהקשה על קרקע
                            ריקה, אבל בר דיווח שהוא "נשאר" — ובצדק: הבסיס צפוף,
                            הפאנל עצמו הוא שכבה שבולעת הקשות (מלכודת 33), ולכן
                            הקשה שנועדה לסגור נופלת חצי מהפעמים על מבנה אחר או
                            על הפאנל. **יציאה שתלויה בכך שתפספס משהו אינה יציאה.**
                        */}
                        <button
                            className="game__buildingClose"
                            type="button"
                            aria-label={t('game_close')}
                            onClick={() => {
                                selectedBuildingRef.current = -1;
                                setRallyingMode(false);
                                setHud((value) => ({
                                    ...value,
                                    selectedBuilding: -1,
                                    confirmSell: false,
                                }));
                            }}
                        >
                            ×
                        </button>
                        <span className="game__buildingName">{hud.selectedBuildingName}</span>
                        <button
                            className="game__button"
                            type="button"
                            onClick={() => {
                                setRallyingMode(true);
                                setNotice(t('build_hint_rally'));
                            }}
                        >
                            {t('build_rally')}
                        </button>
                        {/*
                            תיקון: מתג. **רק כשהמבנה פגוע** — כפתור שלא עושה כלום
                            הוא רעש, וכאן קל להסתיר אותו כי המצב ידוע.
                        */}
                        {hud.selectedBuildingDamaged && (
                            <button
                                className={
                                    hud.selectedBuildingRepairing
                                        ? 'game__button game__button--active'
                                        : 'game__button'
                                }
                                type="button"
                                aria-pressed={hud.selectedBuildingRepairing}
                                onClick={() =>
                                    engine.commands.issue(
                                        {
                                            kind: 'repair',
                                            unitIds: [],
                                            buildingId: selectedBuildingRef.current,
                                        },
                                        engine.store.cur.tick,
                                    )
                                }
                            >
                                {hud.selectedBuildingRepairing
                                    ? t('game_repair_on')
                                    : t('game_repair')}
                            </button>
                        )}
                        {/*
                            מכירה בלתי הפיכה, ולכן בשתי הקשות — כמו "לתפריט".
                        */}
                        <button
                            className={
                                hud.confirmSell
                                    ? 'game__button game__button--active'
                                    : 'game__button'
                            }
                            type="button"
                            onClick={() => {
                                if (!hud.confirmSell) {
                                    setHud((value) => ({ ...value, confirmSell: true }));
                                    setNotice(t('game_sell_confirm'));
                                    return;
                                }
                                engine.commands.issue(
                                    {
                                        kind: 'sell',
                                        unitIds: [],
                                        buildingId: selectedBuildingRef.current,
                                    },
                                    engine.store.cur.tick,
                                );
                                selectedBuildingRef.current = -1;
                                setHud((value) => ({
                                    ...value,
                                    selectedBuilding: -1,
                                    confirmSell: false,
                                }));
                            }}
                        >
                            {hud.confirmSell ? t('game_sell_yes') : t('game_sell')}
                        </button>
                    </div>
                )}

                <div className="game__overlay" dir="auto">
                    <span className="game__mapName">{t(map.def.nameKey)}</span>
                    {/*
                        ההודעה גולשת לשתי שורות במקום להיחתך ב-ellipsis. שורה אחת
                        עם `nowrap` נראתה בטלפון כ"הקשה כפולה לכל ה…" — חצי משפט
                        שאי אפשר להשלים, וזה בדיוק מה שבר ראה.
                    */}
                    <span
                        className={
                            hud.placeError ? 'game__status game__status--error' : 'game__status'
                        }
                    >
                        {hud.notice
                            ? hud.notice
                            : hud.rallying
                            ? t('build_hint_rally')
                            : hud.placing
                            ? hud.placeError
                                ? t('build_hint_bad_spot')
                                : /*
                                     **שם המבנה, לא רק "הקישו למקום".** מרגע שנכנסים
                                     למצב הצבה הפאנל נסגר, ואז אין על המסך שום דבר
                                     שאומר מה בדיוק עומד לנחות — וגודל המבנה הוא מה
                                     שקובע איפה יש לו מקום.
                                  */
                                  `${t('build_hint_place')} · ${hud.placingName}`
                            : /*
                                 מבנה שהושלם וממתין הוא הצעד שהכי קל לפספס: הוא
                                 חוסם את התור, אבל בלי הודעה הוא פשוט נראה כמו
                                 בנייה שנתקעה.
                              */
                              (hud.queues[YARD_QUEUE]?.[0]?.ready ?? false)
                              ? t('build_hint_ready')
                              : hud.selected > 0
                                ? hud.composition ||
                                  (hud.selected === 1
                                      ? t('game_selected_one')
                                      : `${t('game_selected')} ${hud.selected} ${t('game_units')}`)
                                : t('game_hint_select')}
                    </span>
                </div>

                <BuildPanel
                    credits={hud.credits}
                    power={hud.power}
                    queues={hud.queues}
                    onExplainPower={() => setNotice(t('build_power_hint'))}
                    starved={hud.starved}
                    cameos={cameos}
                    canAfford={(kind, type) => hud.credits >= costOf({ kind, type })}
                    available={(kind, type) =>
                        prerequisitesMet({ kind, type }, engine.store.cur.buildings, LOCAL_PLAYER)
                    }
                    onQueue={(kind, type) =>
                        engine.commands.issue(
                            { kind: 'queue', unitIds: [], itemKind: kind, itemType: type },
                            engine.store.cur.tick,
                        )
                    }
                    onCancel={(queueIndex) =>
                        engine.commands.issue(
                            { kind: 'cancel', unitIds: [], queueIndex },
                            engine.store.cur.tick,
                        )
                    }
                    rallying={hud.rallying}
                    onRally={() => setRallyingMode(!rallyingRef.current)}
                    placing={hud.placing}
                    onPlace={() => setPlacingMode(!placingRef.current)}
                />

                {hud.ended && (
                    <div className="game__result" role="status">
                        <div
                            className={
                                hud.winner === LOCAL_PLAYER
                                    ? 'game__resultTitle game__resultTitle--win'
                                    : 'game__resultTitle'
                            }
                        >
                            {hud.winner === LOCAL_PLAYER ? t('result_victory') : t('result_defeat')}
                        </div>
                        {/*
                            שלושה מספרים, לא טבלה. **מסך סיום בלי שום נתון אינו
                            אומר לשחקן אם הקרב היה צמוד או שטיפה**, וזה בדיוק מה
                            שגורם לרצות עוד סיבוב.
                        */}
                        <dl className="game__resultStats">
                            <div>
                                <dt>{t('result_time')}</dt>
                                <dd className="num">{formatGameTime(hud.tick)}</dd>
                            </div>
                            <div>
                                <dt>{t('result_built')}</dt>
                                <dd className="num">{hud.built}</dd>
                            </div>
                            <div>
                                <dt>{t('result_lost')}</dt>
                                <dd className="num">{hud.lost}</dd>
                            </div>
                        </dl>
                        <div className="game__resultActions">
                            {/*
                                "שחק שוב" ראשון ובולט: לחזור לתפריט, לבחור מפה
                                ולבחור קושי מחדש הוא שלוש הקשות כדי לעשות בדיוק
                                את מה שהרגע עשית.
                            */}
                            {onRestart && (
                                <button
                                    className="game__button game__button--active"
                                    type="button"
                                    onClick={onRestart}
                                >
                                    {t('result_again')}
                                </button>
                            )}
                            <button className="game__button" type="button" onClick={onExit}>
                                {t('result_back')}
                            </button>
                        </div>
                    </div>
                )}

                {showDebug && (
                    <div className="game__debug num">
                        <div>fps {hud.fps.toFixed(0)}</div>
                        <div>tick {hud.tick}</div>
                        <div>hash {hud.hash}</div>
                        <div>seed {engine.store.cur.seed}</div>
                        <div>zoom {hud.zoom.toFixed(1)}</div>
                        <div>
                            cam {hud.tileX},{hud.tileY}
                        </div>
                        <div>units {hud.units}</div>
                    </div>
                )}
            </div>

            <div className="game__hud">
                <div className="game__group game__group--top">
                    {/*
                        **יציאה בשתי הקשות, לא באחת.** הכפתור יושב איפה שהאגודל נח
                        וצמוד לכפתורי הקבוצות; הקשה שגויה אחת העיפה את הקרב. אמנם
                        השמירה האוטומטית מצילה בפועל, אבל ברגע האמת אף אחד לא יודע
                        את זה — וזה מרגיש כמו איבוד המשחק.
                    */}
                    <button
                        className={
                            hud.confirmExit
                                ? 'game__button game__button--clear game__button--active'
                                : 'game__button'
                        }
                        type="button"
                        onClick={() => (hud.confirmExit ? onExit() : requestExit())}
                    >
                        {hud.confirmExit ? t('game_exit_yes') : t('game_back_to_menu')}
                    </button>
                    {hud.confirmExit && (
                        <button
                            className="game__button"
                            type="button"
                            onClick={() => {
                                engine.loop.setPaused(false);
                                setHud((value) => ({ ...value, confirmExit: false, notice: '' }));
                            }}
                        >
                            {t('game_resume')}
                        </button>
                    )}
                    {/*
                        קבוצות שליטה. הן יושבות ליד "בחר הכול" בכוונה — שתי הדרכים
                        לבנות בחירה מהירה נמצאות באותו מקום.
                    */}
                    <ControlGroupBar
                        counts={hud.groups}
                        onRecall={recallToSelection}
                        onAssign={assignToGroup}
                    />
                </div>

                {/*
                    פעולות על הבחירה.

                    **הן קיימות תמיד, גם בלי בחירה — מושבתות ולא נעלמות.** קודם הן
                    הוצגו רק כשהייתה בחירה, מתוך מחשבה שכפתור מושבת מבזבז רוחב יקר
                    בטלפון. בפועל זה גרם לכל הסרגל להתרחב ולהתכווץ בכל הקשה: כפתורים
                    קפצו למקום אחר בדיוק ברגע שהאצבע ירדה עליהם. בר דיווח על זה, וזה
                    צודק — **פריסה יציבה שווה יותר משטח פנוי.**
                */}
                <div className="game__group game__group--selection">
                    {/*
                        בחירת כל הכוח בלחיצה אחת. במגע אין Shift ואין גרירה נוחה,
                        וזו הפעולה שהכי הרבה פעמים רוצים אותה במשחק טלפון. היא
                        יושבת כאן, בשורת הפעולות על הבחירה, כי זו הפעולה שמתחילה
                        את הרצף — ולא בשורת המערכת.
                    */}
                    <button className="game__button" type="button" onClick={selectAll}>
                        {t('game_select_all')}
                    </button>
                    {/*
                        ריקון בחירה. במגע אין דרך אחרת: הקשה על קרקע ריקה חייבת
                        להיות פקודת תנועה, אחרת אי אפשר להזיז כוח בלי כפתור ימני.
                    */}
                    <button
                        className="game__button game__button--clear"
                        type="button"
                        disabled={hud.selected === 0}
                        onClick={() => {
                            selectedRef.current = new Set();
                            setHud((value) => ({ ...value, selected: 0 }));
                        }}
                    >
                        {t('game_deselect')}
                    </button>

                    {/*
                        תקוף-נוע ועצור — פקודות, לא מצבים. אין כאן מתג שצריך
                        לזכור באיזה מצב הוא: לוחצים, וזה קורה.
                    */}
                    <button
                        className={
                            hud.attackMove ? 'game__button game__button--active' : 'game__button'
                        }
                        type="button"
                        disabled={hud.selected === 0}
                        onClick={() => {
                            /*
                             * גם תקוף-נוע חוטף את ההקשה הבאה — ומאחר שהוא נבדק
                             * **אחרי** הצבה ונקודת התכנסות, הדלקה שלו בזמן
                             * שאחד מהם דלוק פשוט לא הייתה עושה כלום.
                             */
                            setPlacingMode(false);
                            setRallyingMode(false);
                            attackMoveRef.current = true;
                            setHud((value) => ({ ...value, attackMove: true }));
                        }}
                        aria-pressed={hud.attackMove}
                    >
                        {t('game_attack_move')}
                    </button>
                    <button
                        className="game__button"
                        type="button"
                        disabled={hud.selected === 0}
                        onClick={() =>
                            engine.commands.issue(
                                {
                                    kind: 'stop',
                                    unitIds: [...selectedRef.current].sort((a, b) => a - b),
                                },
                                engine.store.cur.tick,
                            )
                        }
                    >
                        {t('game_stop')}
                    </button>
                </div>

                <span className="game__spacer" />

                <div className="game__group game__group--top">
                    {/*
                        מהירות משחק. אינה "מריצה את הזמן מהר יותר" אלא מריצה יותר טיקים
                        זהים — ולכן 4x זהה ביט-לביט ל-1x, ונבדק ככזה ב-GameLoop.test.
                    */}
                    <button
                        className="game__button num"
                        type="button"
                        onClick={() => {
                            const next = engine.loop.speed >= 4 ? 1 : engine.loop.speed * 2;
                            engine.loop.setSpeed(next);
                            setHud((value) => ({ ...value, speed: next }));
                        }}
                    >
                        {hud.speed}×
                    </button>
                    <button
                        className="game__button game__button--toggle"
                        type="button"
                        onClick={() => engine.loop.setPaused(!engine.loop.isPaused)}
                    >
                        {hud.paused ? t('game_resume') : t('game_pause')}
                    </button>
                    {/*
                        **כפתורי הזום נמחקו.** הם הוסתרו ממילא מתחת ל-480px, כלומר
                        התקיימו רק במקום שבו יש גלגלת ממשית. צביטה בשתי אצבעות
                        וגלגלת מכסות את התפקיד, והמשבצת שהתפנתה שווה יותר.
                    */}
                    {/*
                        **"מרכוז" נמחק, לא הוסתר.**

                        הוא היה מרונדר בתנאי (`hud.selected === 0`), כלומר נעלם ברגע
                        שיש בחירה — וזו בדיוק מלכודת 27 שחזרה לקוד: כל הסרגל התארגן
                        מחדש בדיוק ברגע שהאצבע ירדה. גם התפקיד שלו נעלם: המפה
                        המוקטנת היא הדרך לקפוץ לכל מקום.
                    */}
                </div>
            </div>
        </div>
    );
}
